import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ConflictError, fetchWithAuth, getErrorMessage } from '../lib/api'
import { queryKeys } from '../lib/queryKeys'
import type {
  AfvinkResponse,
  BulkRecategorizeRequest,
  Categorisation,
  ListResponse,
  RecategorizeRequest,
  SamenvattingType,
  TerugzetResponse,
} from '../types'

const PAGINA = 25

/**
 * Volgende offset voor infinite-scroll pagina's, of undefined als er niets meer te laden is.
 * Een pagina korter dan de paginagrootte betekent altijd het einde — ook als `total` (nog)
 * niet is meegezakt, bijvoorbeeld doordat de wachtrij tussen twee laadbeurten kromp. Zonder
 * die check blijft `hasNextPage` waar en herhaalt "meer laden" oneindig dezelfde offset.
 */
export function volgendeOffset(
  last: ListResponse<Categorisation>,
  pages: ListResponse<Categorisation>[],
) {
  if (last.items.length < PAGINA) return undefined
  const geladen = pages.reduce((n, p) => n + p.items.length, 0)
  return geladen < last.total ? geladen : undefined
}

/**
 * Fetch list of categorisations with optional filters.
 * Params are included in queryKey for proper cache separation.
 */
export function useCategorisations(params?: Record<string, string>) {
  return useQuery({
    queryKey: queryKeys.categorisations.list(params),
    queryFn: () => api.getCategorisations(params),
  })
}

/**
 * Fetch items needing review (below confidence threshold and needs_review=true).
 */
export function useReviewQueue() {
  return useQuery({
    queryKey: queryKeys.categorisations.reviewQueue(),
    queryFn: api.getReviewQueue,
  })
}

/**
 * Fetch the publication queue (G-05): confirmed meetings still awaiting a human
 * decision before anything reaches the Obsidian vaults.
 *
 * Uses the dedicated `/publication-queue` endpoint, not the generic list route
 * with a `publication_status` filter: the queue has its own two-column sort
 * ("twijfelgevallen eerst", then oldest first) that the generic route's single
 * `sort_by`/`sort_order` cannot express. Without it, the oldest waiting
 * meeting — exactly the one the stale-approval alert names — could fall off
 * the newest-first default page.
 */
export function usePublicationQueue() {
  return useQuery({
    queryKey: queryKeys.categorisations.publicationQueue(),
    queryFn: async (): Promise<ListResponse<Categorisation>> => {
      const res = await fetchWithAuth('/api/categorisations/publication-queue')
      if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to fetch publication queue'))
      return res.json()
    },
  })
}

/**
 * Shared mutation for the three wachtrij decisions (G-14). Invalidates
 * `categorisations.all` so list, detail and the publication queue all refresh.
 * Generic because `/afvinken` answers with `AfvinkResponse`, `/uit-vault` with the
 * updated `Categorisation` and `/restore` with `TerugzetResponse` (per-file outcome).
 */
function usePublicationMutation<TResult>(
  action: 'afvinken' | 'uit-vault' | 'restore',
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (vars: string): Promise<TResult> => {
      const res = await fetchWithAuth(`/api/categorisations/${vars}/${action}`, {
        method: 'POST',
      })
      if (res.status === 409) {
        throw new ConflictError(await getErrorMessage(res, `Failed to ${action} categorisation`))
      }
      if (!res.ok) throw new Error(await getErrorMessage(res, `Failed to ${action} categorisation`))
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.voorstellen.all })
      // Afvinken kan een prospect- of partnermap aanmaken; die hoort direct in de keuzelijst (G-12).
      if (action === 'afvinken') {
        queryClient.invalidateQueries({ queryKey: queryKeys.subcategories.all })
      }
      // Afvinken en terugzetten schrijven in de entiteitsmappen (een nieuwe map, of
      // gesprekken die erbij komen); de vaultentiteiten tonen dat meteen (G-14).
      if (action === 'afvinken' || action === 'restore') {
        queryClient.invalidateQueries({ queryKey: queryKeys.vaultEntities.all })
      }
    },
  })
}

/** De wachtrij (G-06), in pagina's van 25; twijfelgevallen eerst, dan oudste eerst. */
export function useWachtrij() {
  return useInfiniteQuery({
    queryKey: queryKeys.categorisations.wachtrij(),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<ListResponse<Categorisation>> => {
      const res = await fetchWithAuth(
        `/api/categorisations/publication-queue?limit=${PAGINA}&offset=${pageParam}`,
      )
      if (!res.ok) throw new Error(await getErrorMessage(res, 'Wachtrij laden mislukt'))
      return res.json()
    },
    getNextPageParam: volgendeOffset,
  })
}

/** Besloten gesprekken (G-06): bevestigd of afgewezen, nieuwste eerst. */
export function useVerwerkt(needsReview: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.categorisations.verwerkt(needsReview),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.getCategorisations({
        publication_status: 'verwerkt',
        limit: String(PAGINA),
        offset: String(pageParam),
        sort_by: 'created_at',
        sort_order: 'desc',
        ...(needsReview ? { needs_review: 'true' } : {}),
      }),
    getNextPageParam: volgendeOffset,
  })
}

/** Bewerkingen aan een wachtende samenvatting opslaan (G-06). */
export function useSamenvattingOpslaan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      samenvatting_type,
      velden,
    }: {
      id: string
      samenvatting_type: SamenvattingType | null
      velden: Record<string, unknown>
    }) => api.saveSamenvatting(id, samenvatting_type, velden),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all }),
  })
}

/** Bewerkingen ongedaan maken en terug naar de LLM-samenvatting (G-06). */
export function useSamenvattingTerugzetten() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.resetSamenvatting(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all }),
  })
}

/** Alleen de samenvatting opnieuw laten maken, in het type van de huidige categorie (A-20). */
export function useSamenvattingOpnieuw() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.samenvattingOpnieuw(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all }),
  })
}

/** Afvinken: het gesprek staat al in de vault en gaat uit de wachtrij (G-14). */
export const useAfvinken = () => usePublicationMutation<AfvinkResponse>('afvinken')
/** Verwijder uit vault: haalt transcript en samenvatting weg; het gesprek wordt afgewezen. */
export const useUitVault = () => usePublicationMutation<Categorisation>('uit-vault')
/** Zet een afgewezen gesprek terug in de vault en in de wachtrij. */
export const useRestore = () => usePublicationMutation<TerugzetResponse>('restore')

/**
 * Approve a categorisation as correct, removing it from the review queue.
 * Invalidates all categorisation queries on success to refresh data.
 */
export function useApproveCategorisation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.approveCategorisation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all })
    },
  })
}

/**
 * Approve multiple categorisations at once.
 * Invalidates all categorisation queries on success to refresh data.
 */
export function useBatchApprove() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => api.batchApproveCategorisations(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all })
    },
  })
}

/**
 * Fetch a single categorisation by ID.
 */
export function useCategorisation(id: string) {
  return useQuery({
    queryKey: queryKeys.categorisations.detail(id),
    queryFn: () => api.getCategorisation(id),
    enabled: !!id,
  })
}

/**
 * Fetch correction history for a categorisation.
 */
export function useCorrectionHistory(categorisationId: string) {
  return useQuery({
    queryKey: queryKeys.categorisations.corrections(categorisationId),
    queryFn: () => api.getCorrections(categorisationId),
    enabled: !!categorisationId,
  })
}

/**
 * Recategorize a single categorisation. Creates correction audit log and updates vault.
 * Invalidates all categorisation queries on success.
 */
export function useRecategorize() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...data }: RecategorizeRequest & { id: string }) =>
      api.recategorize(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all })
    },
  })
}

/**
 * Reprocess a categorisation via LLM. Creates a webhook event for the worker to pick up.
 * Invalidates categorisation and webhook event queries on success.
 */
export function useReprocess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.reprocess(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.webhookEvents.all })
    },
  })
}

/**
 * Bulk recategorize multiple categorisations at once.
 * Invalidates all categorisation queries on success.
 */
export function useBulkRecategorize() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkRecategorizeRequest) => api.bulkRecategorize(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.categorisations.all })
    },
  })
}
